import React, { useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { globalSearch } from 'wasp/client/operations';
import {
  Search, X, Landmark, Users, Megaphone, Calendar, Building2, MapPin,
  ShoppingCart, Utensils, ShoppingBag, Wrench, ArrowRight,
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { locationScopedPath } from '../../client/lib/locationPaths';
import { cn } from '../../lib/utils';
import { LocationWithHierarchy } from '../../client/contexts/LocationContext';

interface GlobalSearchResult {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: string;
  section: 'organization' | 'community';
  metadata?: any;
}

const communityTypeConfig: Record<string, { icon: typeof Search; label: string; color: string }> = {
  NOTICE:     { icon: Megaphone,    label: 'Notices',      color: 'text-purple-500' },
  EVENT:      { icon: Calendar,     label: 'Events',       color: 'text-blue-500' },
  TENANT:     { icon: Building2,    label: 'Tenants',      color: 'text-cyan-600' },
  FACILITY:   { icon: MapPin,       label: 'Facilities',   color: 'text-teal-500' },
  BUY_SELL:   { icon: ShoppingCart, label: 'Buy & Sell',   color: 'text-green-600' },
  FOOD_COURT: { icon: Utensils,     label: 'Food Court',   color: 'text-orange-500' },
  LOST_FOUND: { icon: Search,       label: 'Lost & Found', color: 'text-blue-400' },
  RETAIL:     { icon: ShoppingBag,  label: 'Marketplace',  color: 'text-pink-500' },
  SERVICE:    { icon: Wrench,       label: 'Services',     color: 'text-purple-400' },
};

const communityNavItems: { key: string; label: string; type: string }[] = [
  { key: 'notices',    label: 'Notices',      type: 'NOTICE'     },
  { key: 'events',     label: 'Events',       type: 'EVENT'      },
  { key: 'tenants',    label: 'Tenants',      type: 'TENANT'     },
  { key: 'facilities', label: 'Facilities',   type: 'FACILITY'   },
  { key: 'buySell',    label: 'Buy & Sell',   type: 'BUY_SELL'   },
  { key: 'foodCourt',  label: 'Food Court',   type: 'FOOD_COURT' },
  { key: 'lostFound',  label: 'Lost & Found', type: 'LOST_FOUND' },
  { key: 'retail',     label: 'Marketplace',  type: 'RETAIL'     },
  { key: 'services',   label: 'Services',     type: 'SERVICE'    },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLocation } = useLocationContext() as any as { selectedLocation: LocationWithHierarchy | null };
  const [activeSection, setActiveSection] = useState<'organization' | 'community' | null>(null);
  const [inputValue, setInputValue] = useState(searchParams.get('q') || '');

  const originPath: string = (location.state as any)?.from || '/';

  const searchTerm = searchParams.get('q') || '';

  const { data: searchData, isFetching: isSearching } = useQuery(
    globalSearch,
    {
      locationId: selectedLocation?.id || '',
      searchTerm,
      limit: 50,
    },
    {
      enabled: searchTerm.trim().length >= 2,
    }
  );

  const results = searchData?.results || [];
  const byType = searchData?.byType || {
    organizations: 0, notices: 0, events: 0, tenants: 0, facilities: 0,
    buySell: 0, foodCourt: 0, lostFound: 0, retail: 0, services: 0,
  };
  const total = searchData?.total || 0;
  const isLoading = isSearching && searchTerm.trim().length >= 2;

  // Debounce input to URL param
  React.useEffect(() => {
    if (inputValue.trim().length < 2) {
      if (searchParams.get('q')) {
        setSearchParams({}, { replace: true, state: location.state });
      }
      return;
    }
    const timer = setTimeout(() => {
      setSearchParams({ q: inputValue.trim() }, { replace: true, state: location.state });
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() }, { state: location.state });
      setActiveSection(null);
    }
  };

  const handleClose = () => {
    navigate(originPath, { replace: true });
  };

  const handleClear = () => {
    setInputValue('');
    setSearchParams({}, { replace: true, state: location.state });
  };

  const handleResultClick = (result: GlobalSearchResult) => {
    if (result.section === 'community' && selectedLocation) {
      navigate(locationScopedPath(selectedLocation, result.url));
    } else {
      navigate(result.url);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const orgResults = results.filter((r: GlobalSearchResult) => r.section === 'organization');
  const communityResults = results.filter((r: GlobalSearchResult) => r.section === 'community');
  const communityGrouped = communityResults.reduce((acc: Record<string, GlobalSearchResult[]>, r: GlobalSearchResult) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, GlobalSearchResult[]>);

  const showOrgs = activeSection === null || activeSection === 'organization';
  const showCommunity = activeSection === null || activeSection === 'community';
  const communityTotal = Object.entries(byType)
    .filter(([k]) => k !== 'organizations')
    .reduce((s, [, v]) => s + v, 0);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-content-bg">
        {/* Search header bar */}
        <div className="sticky top-0 z-10 bg-content-bg border-b border-border px-6 py-4">
          <form onSubmit={handleSearch} className="flex items-center gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-input-placeholder" />
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Search organizations, events, notices..."
                className="w-full pl-10 pr-10 py-2.5 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                autoFocus
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {inputValue === searchTerm && searchTerm ? (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-4 py-2.5 bg-sidebar-hover text-content-text rounded-lg text-sm font-medium hover:bg-border transition-colors"
                >
                  Clear
                </button>
              ) : (
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Search
                </button>
              )}
              
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2.5 bg-sidebar-hover text-content-text rounded-lg text-sm font-medium hover:bg-border transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>
          </form>
          {searchTerm && !isLoading && (
            <p className="text-xs text-content-text-muted mt-2">
              {total > 0
                ? <>{total} result{total !== 1 ? 's' : ''} for <span className="font-medium text-content-text">"{searchTerm}"</span></>
                : <>No results for <span className="font-medium text-content-text">"{searchTerm}"</span></>
              }
            </p>
          )}
        </div>

        {/* Body */}
        {!searchTerm ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <Search className="w-12 h-12 text-content-text-muted" />
            <p className="font-semibold text-lg text-content-text">Search Gedoba</p>
            <p className="text-sm text-content-text-muted max-w-xs">
              Find organizations, community notices, events, and more.
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-content-text-muted">Searching...</p>
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <Search className="w-12 h-12 text-content-text-muted" />
            <p className="font-semibold text-content-text">No results found</p>
            <p className="text-sm text-content-text-muted max-w-xs">
              Try different keywords or broader terms.
            </p>
          </div>
        ) : (
          <div className="flex">
            {/* Main results */}
            <div className="flex-1 px-6 py-6 space-y-8 min-w-0">
              {/* Organizations */}
              {showOrgs && orgResults.length > 0 && (
                <section id="section-organizations">
                  <SectionHeading icon={Landmark} label="Organizations" count={orgResults.length} />
                  <div className="mt-3 divide-y divide-border border border-border rounded-lg overflow-hidden">
                    {orgResults.map(result => (
                      <OrgResultRow key={result.id} result={result} onClick={() => handleResultClick(result)} />
                    ))}
                  </div>
                </section>
              )}

              {/* Community */}
              {showCommunity && communityResults.length > 0 && (
                <section id="section-community">
                  <SectionHeading icon={Users} label="Community" count={communityResults.length} />
                  <div className="mt-3 space-y-6">
                    {communityNavItems
                      .filter(({ type }) => communityGrouped[type]?.length > 0)
                      .map(({ type, label }) => {
                        const cfg = communityTypeConfig[type];
                        const Icon = cfg.icon;
                        const items = communityGrouped[type];
                        return (
                          <div key={type} id={`section-${type.toLowerCase()}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className={cn('w-4 h-4', cfg.color)} />
                              <h3 className="text-sm font-semibold text-content-text">{label}</h3>
                              <span className="text-xs text-content-text-muted">({items.length})</span>
                            </div>
                            <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                              {items.map(result => (
                                <ContentResultRow
                                  key={result.id}
                                  result={result}
                                  onClick={() => handleResultClick(result)}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </section>
              )}
            </div>

            {/* Right filter sidebar */}
            <aside className="w-52 flex-shrink-0 border-l border-border bg-sidebar-bg px-3 py-5 space-y-1 sticky top-[73px] self-start max-h-[calc(100vh-73px)] overflow-y-auto">
              <p className="text-[10px] font-semibold text-content-text-muted uppercase tracking-wider px-2 pb-2">
                Filter
              </p>

              <NavButton
                active={activeSection === null}
                onClick={() => setActiveSection(null)}
                label="All Results"
                count={total}
              />

              {byType.organizations > 0 && (
                <NavButton
                  active={activeSection === 'organization'}
                  onClick={() => {
                    setActiveSection(prev => prev === 'organization' ? null : 'organization');
                    scrollToSection('section-organizations');
                  }}
                  label="Organizations"
                  count={byType.organizations}
                  icon={Landmark}
                  iconColor="text-primary"
                />
              )}

              {communityTotal > 0 && (
                <>
                  <NavButton
                    active={activeSection === 'community'}
                    onClick={() => {
                      setActiveSection(prev => prev === 'community' ? null : 'community');
                      scrollToSection('section-community');
                    }}
                    label="Community"
                    count={communityTotal}
                    icon={Users}
                    iconColor="text-content-text-muted"
                  />

                  {(activeSection === null || activeSection === 'community') &&
                    communityNavItems
                      .filter(({ key }) => (byType as any)[key] > 0)
                      .map(({ key, label, type }) => {
                        const cfg = communityTypeConfig[type];
                        const Icon = cfg.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => scrollToSection(`section-${type.toLowerCase()}`)}
                            className="w-full flex items-center justify-between gap-1.5 px-2 py-1 pl-6 rounded text-xs text-content-text-muted hover:text-content-text hover:bg-sidebar-hover transition-colors"
                          >
                            <span className="flex items-center gap-1.5 min-w-0">
                              <Icon className={cn('w-3 h-3 flex-shrink-0', cfg.color)} />
                              <span className="truncate">{label}</span>
                            </span>
                            <span className="flex-shrink-0 text-[10px] bg-content-bg border border-border rounded-full px-1.5 py-0.5">
                              {(byType as any)[key]}
                            </span>
                          </button>
                        );
                      })}
                </>
              )}
            </aside>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function SectionHeading({ icon: Icon, label, count }: { icon: any; label: string; count: number }) {
  return (
    <div className="flex items-center gap-2 border-b border-border pb-2">
      <Icon className="w-4 h-4 text-primary" />
      <h2 className="font-bold text-base text-content-text">{label}</h2>
      <span className="text-sm text-content-text-muted">({count})</span>
    </div>
  );
}

function NavButton({
  active, onClick, label, count, icon: Icon, iconColor,
}: {
  active: boolean; onClick: () => void; label: string; count: number;
  icon?: any; iconColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors',
        active ? 'bg-primary/10 text-primary' : 'text-content-text hover:bg-sidebar-hover'
      )}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {Icon && <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', iconColor)} />}
        <span className="truncate">{label}</span>
      </span>
      <span className={cn(
        'flex-shrink-0 text-[10px] rounded-full px-1.5 py-0.5',
        active ? 'bg-primary/20 text-primary' : 'bg-content-bg border border-border text-content-text-muted'
      )}>
        {count}
      </span>
    </button>
  );
}

function OrgResultRow({ result, onClick }: { result: GlobalSearchResult; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 bg-content-bg hover:bg-sidebar-hover transition-colors group flex items-center justify-between gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-content-text group-hover:text-primary transition-colors">
            {result.title}
          </span>
          {result.metadata?.orgType && (
            <span className="text-[10px] bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5">
              {result.metadata.orgType}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {(result.metadata?.cityName || result.metadata?.provinceCode) && (
            <span className="text-xs text-content-text-muted">
              {[result.metadata.cityName, result.metadata.provinceCode].filter(Boolean).join(', ')}
            </span>
          )}
          {result.description && (
            <span className="text-xs text-content-text-muted line-clamp-1">{result.description}</span>
          )}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-content-text-muted group-hover:text-primary transition-colors flex-shrink-0" />
    </button>
  );
}

function ContentResultRow({ result, onClick }: { result: GlobalSearchResult; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 bg-content-bg hover:bg-sidebar-hover transition-colors group flex items-center justify-between gap-4"
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-content-text group-hover:text-primary transition-colors line-clamp-1">
          {result.title}
        </p>
        {result.description && (
          <p className="text-xs text-content-text-muted mt-0.5 line-clamp-1">{result.description}</p>
        )}
      </div>
      <ArrowRight className="w-4 h-4 text-content-text-muted group-hover:text-primary transition-colors flex-shrink-0" />
    </button>
  );
}



